import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveService } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Service, ServiceInput } from '../../types';
import { normalizeForm, SERVICE_TYPE_LABELS, toDateInput, toOptions } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect } from './EntitySelects';
import { t } from '../../i18n';

type FormValues = Record<keyof ServiceInput, string>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  service?: Service | null;
  carId?: number;
}

export function ServiceFormModal({ isOpen, onClose, service, carId }: Props) {
  const save = useSaveService();
  const carLocked = !!carId || !!service;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    reset({
      car_id: String(service?.car_id ?? carId ?? ''),
      service_date: service?.service_date ?? toDateInput(),
      service_type: service?.service_type ?? 'maintenance',
      description: service?.description ?? '',
      km_at_service: service?.km_at_service?.toString() ?? '',
      cost: service?.cost?.toString() ?? '',
      workshop: service?.workshop ?? '',
      invoice_number: service?.invoice_number ?? '',
      next_service_date: service?.next_service_date ?? '',
      next_service_km: service?.next_service_km?.toString() ?? '',
    });
  }, [isOpen, service, carId, reset]);

  const onSubmit = async (values: FormValues) => {
    const data = normalizeForm(values, ['car_id', 'km_at_service', 'cost', 'next_service_km']) as unknown as ServiceInput;
    // Disabled (locked) car select is not included in the submitted values
    data.car_id = service?.car_id ?? carId ?? data.car_id;
    try {
      await save.mutateAsync({ id: service?.id, data });
      toast.success(service ? t('Servisný záznam bol uložený') : t('Servisný záznam bol vytvorený'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Servisný záznam sa nepodarilo uložiť')));
    }
  };

  const nonNegative = (v: string) => !v || Number(v.replace(',', '.')) >= 0 || t('Nesmie byť záporné');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      title={service ? t('Upraviť servisný záznam') : t('Nový servisný záznam')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="service-form" loading={save.isPending}>
            {service ? t('Uložiť') : t('Vytvoriť')}
          </Button>
        </>
      }
    >
      <form id="service-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect
          label={t('Vozidlo')}
          required
          disabled={carLocked}
          wrapperClassName="sm:col-span-2"
          error={errors.car_id?.message}
          {...register('car_id', { required: carLocked ? false : t('Vyberte vozidlo') })}
        />
        <Input label={t('Dátum servisu')} type="date" required error={errors.service_date?.message} {...register('service_date', { required: t('Zadajte dátum') })} />
        <Select label={t('Typ')} required options={toOptions(SERVICE_TYPE_LABELS)} {...register('service_type')} />
        <Textarea label={t('Popis prác')} wrapperClassName="sm:col-span-2" {...register('description')} />
        <Input label={t('Stav km pri servise')} type="number" min={0} error={errors.km_at_service?.message} {...register('km_at_service', { validate: nonNegative })} />
        <Input label={t('Cena (€)')} type="number" step="0.01" min={0} error={errors.cost?.message} {...register('cost', { validate: nonNegative })} />
        <Input label={t('Servis / dielňa')} {...register('workshop')} />
        <Input label={t('Číslo faktúry')} {...register('invoice_number')} />
        <div className="sm:col-span-2">
          <p className="mb-2 mt-2 text-sm font-medium text-secondary-900">{t('Plán ďalšej údržby')}</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label={t('Ďalší servis – dátum')} type="date" {...register('next_service_date')} />
            <Input
              label={t('Ďalší servis – pri stave km')}
              type="number"
              min={0}
              error={errors.next_service_km?.message}
              {...register('next_service_km', { validate: nonNegative })}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
